import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Loader2,
  Users,
  Share2,
  DollarSign,
  Award,
  ArrowLeft,
  Copy,
  Check,
  Network,
  User as UserIcon,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

interface NetworkNode {
  phone?: string;
  name?: string;
  depth?: number;
  totalOrderValue?: number;
  joinedAt?: string;
}

interface DashboardData {
  referralCode: string;
  totalCommission: number;
  paidCommission: number;
  unpaidCommission: number;
  directCount: number;
  network?: NetworkNode[];
}

export default function ReferralDashboard() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<DashboardData | null>(null);
  const [network, setNetwork] = useState<NetworkNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('缺少訪問令牌');
      setLoading(false);
      return;
    }

    const loadDashboard = async () => {
      try {
        const res = await apiFetch(`/api/public/referral/dashboard/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `無法載入儀表板 (${res.status})`);
        }
        const dashboard: DashboardData = await res.json();
        setData(dashboard);

        const netRes = await apiFetch(`/api/public/referral/dashboard/${token}/network`);
        if (netRes.ok) {
          const netData = await netRes.json();
          setNetwork(Array.isArray(netData.network) ? netData.network : Array.isArray(netData) ? netData : []);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : '載入失敗');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [token]);

  const shareUrl = data?.referralCode
    ? `${window.location.origin}/?gs=${encodeURIComponent(data.referralCode)}`
    : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="animate-spin text-orange-600" size={40} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full text-center">
          <p className="text-red-600 dark:text-red-400 font-medium">{error || '無法載入資料'}</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition"
          >
            <ArrowLeft size={18} /> 返回首頁
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-orange-600 dark:text-orange-400 hover:underline mb-6"
        >
          <ArrowLeft size={18} /> 返回首頁
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white shadow-lg">
            <Award size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">我的推薦獎賞</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">推薦好友，共享美味</p>
          </div>
        </div>

        {/* Referral code card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">我的推薦碼</p>
              <div className="text-3xl font-bold text-orange-600 tracking-wider">{data.referralCode}</div>
            </div>
            <div className="flex-1 sm:max-w-xs">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">分享連結</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 bg-orange-600 text-white text-sm font-bold rounded-lg hover:bg-orange-700 transition"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? '已複製' : '複製'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <DollarSign size={16} /> 總佣金
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">HK${data.totalCommission}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <Check size={16} /> 已發放
            </div>
            <div className="text-2xl font-bold text-green-600">HK${data.paidCommission}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <DollarSign size={16} /> 待發放
            </div>
            <div className="text-2xl font-bold text-orange-600">HK${data.unpaidCommission}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
              <Users size={16} /> 直接推薦
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{data.directCount}</div>
          </div>
        </div>

        {/* Network tree */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <Network size={20} className="text-orange-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">推薦網絡</h2>
          </div>

          {network.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              暫時沒有推薦記錄，快把連結分享給朋友吧！
              <Share2 size={32} className="mx-auto mt-4 text-orange-200 dark:text-orange-900/40" />
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {network.map((node, idx) => (
                <li key={idx} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                      <UserIcon size={18} />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white text-sm">
                        {node.name || node.phone || '匿名用戶'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {node.depth !== undefined ? `第 ${node.depth + 1} 層` : '直接推薦'}
                        {node.joinedAt ? ` · ${new Date(node.joinedAt).toLocaleDateString('zh-HK')}` : ''}
                      </div>
                    </div>
                  </div>
                  {node.totalOrderValue !== undefined && (
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        HK${node.totalOrderValue}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">累計消費</div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
